import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { client, HttpException, type components, type operations } from '@/libs/openapi';
import { queryClient } from '@/main';
import type { QueryFnResult, QueryResultData } from '@/types/query-result-data.type';
import { Resource } from '@/types/resource.type';

type OrganizationTeamListData = components['schemas']['ManyOrganizationTeamsResponseDto'];

export const organizationTeamQueries = queryNamespace(Resource.organizationTeam, {
  list: (params?: operations['OrganizationTeamController_getMany']['parameters']['query']) => queryOptions({
    queryKey: [ params ],
    queryFn: ({ signal }) =>
      client.GET('/customer/api/tcd/teams', {
        signal,
        params: { query: { limit: params?.limit ?? 100, page: params?.page ?? 1, name: params?.name, sortBy: params?.sortBy, id: params?.id }},
      }).then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({
            message: Array.isArray(error.message) ? error.message[0] : error.message,
            response,
            isGlobal: false,
          });
        }

        return data;
      }),
    select: (data) => ({
      totalCount: data.totalCount,
      data: data.items.map((record) => ({
        original: record,
      })),
    }),
  }),
});

export const organizationTeamMutations = mutationNamespace(Resource.organizationTeam, {
  create: mutationOptions({
    mutationFn: ({ name }: { name: string }) =>
      client.POST('/customer/api/tcd/teams', {
        body: { name },
      }).then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({
            message: Array.isArray(error.message) ? error.message[0] : error.message,
            response,
            isGlobal: false,
          });
        }

        return data;
      }),
    onMutate: async ({ name }) => {
      await queryClient.cancelQueries({ queryKey: organizationTeamQueries.all.queryKey });

      const previousLists = queryClient.getQueriesData<OrganizationTeamListData>({ queryKey: organizationTeamQueries.all.queryKey });

      queryClient.setQueriesData<OrganizationTeamListData>(
        { queryKey: organizationTeamQueries.all.queryKey },
        (old) => (old && {
          ...old,
          totalCount: old.totalCount + 1,
          items: [ ...old.items, { id: -1, name } as components['schemas']['OrganizationTeamResponseDto'] ],
        }),
      );
      return { previousLists };
    },
    onError: (_err, _body, context) => {
      context?.previousLists?.forEach(([ queryKey, data ]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: organizationTeamQueries.all.queryKey });
    },
  }),
});

export type OrganizationTeamQueryFnResult = QueryFnResult<typeof organizationTeamQueries.list>;
export type OrganizationTeamQueryResultData = QueryResultData<typeof organizationTeamQueries.list>;
